/**
 * DECISIONS 200 step 5 (C1; the owner: drop pods "more expensive or unique or consumible because they're theoretically an
 * expensive capability"): a side holds N pod drops for the match (MatchSetup.podsByTeam). A call at a point anywhere on the
 * battle planet spends one and takes up to PODS.squad of the side's units waiting to respawn (the waves' pool: dead, a ticket
 * each, not a seat's, not aboard a dropship or waiting for one); PODS.prepSeconds later they come down in pods in a ring at the
 * point (the Match: a respawn each, its ticket, its kit, the lander), one drop squad with the caller's order (core/dropSquad.ts).
 * The fast, deep insertion beside the dropship's slow, seen one. Pure; with no side holding any the field is absent.
 */
import type { GameState, PodDrop, SquadOrder, Vec3 } from '../types';
import { PODS, PHYS_DT } from '../constants';
import { vecNormalize } from '../math';
import { battlePlanetIndex } from './actionSetup';
import { waitingRiders } from './dropship';

/** How many a side has left (0 with none held). */
export function podsLeft(state: Pick<GameState, 'pods'>, team: number): number {
  return state.pods?.left[team] ?? 0;
}

/** The state with each side's count (absent when no side holds any: nothing changes). */
export function withPods(state: GameState, byTeam: readonly number[] | undefined): GameState {
  if (!byTeam?.some(n => n > 0)) return state;
  const left: Record<number, number> = {};
  byTeam.forEach((n, t) => { if (n > 0) left[t] = Math.floor(n); });
  return { ...state, pods: { left, issued: { ...left }, drops: [], next: 0 } };
}

/** A side's call at a point: refused with none left, off the battle planet, or nobody waiting; else one spent and the drop on its way. */
export function callPods(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }, tick: number, by?: 'human' | 'commander', order?: SquadOrder): { state: GameState; drop: PodDrop } | { refused: string } {
  const a = state.pods;
  if (!a || (a.left[team] ?? 0) <= 0) return { refused: 'No pod drops left' };
  if (!state.planets[at.planetIndex] || at.planetIndex !== battlePlanetIndex(state)) return { refused: 'Not on the battle planet' };
  const units = waitingRiders(state, team).slice(0, PODS.squad);
  if (!units.length) return { refused: 'Nobody waiting to come in' };
  const drop: PodDrop = { id: a.next, team, planetIndex: at.planetIndex, at: vecNormalize(at.surfaceDir), units, dropTick: tick + Math.max(1, Math.round(PODS.prepSeconds / PHYS_DT)), ...(by ? { by } : {}), ...(order ? { order } : {}) };
  return { state: { ...state, pods: { ...a, left: { ...a.left, [team]: (a.left[team] ?? 0) - 1 }, drops: [...a.drops, drop], next: a.next + 1 } }, drop };
}

/** The drops due at `tick` (taken off the state: the Match brings their units down). */
export function stepPods(state: GameState, tick: number): { state: GameState; due: PodDrop[] } {
  const a = state.pods;
  if (!a?.drops.length || !a.drops.some(d => d.dropTick <= tick)) return { state, due: [] };
  return { state: { ...state, pods: { ...a, drops: a.drops.filter(d => d.dropTick > tick) } }, due: a.drops.filter(d => d.dropTick <= tick) };
}
